import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import resourcesToBackend from 'i18next-resources-to-backend'

// German is the primary market and the fallback language: bundle it eagerly so
// the default experience never flashes untranslated keys. All other languages
// are code-split per namespace and fetched on demand (saves ~250 KB in the
// entry chunk; until a chunk arrives, i18next falls back to German).
import deCommon from './locales/de/common.json'
import deAuth from './locales/de/auth.json'
import deMenuEditor from './locales/de/menuEditor.json'
import deRestaurants from './locales/de/restaurants.json'
import deOpeningTimes from './locales/de/openingTimes.json'
import deHolidaySettings from './locales/de/holidaySettings.json'
import deOrders from './locales/de/orders.json'
import deDeliveryAreas from './locales/de/deliveryAreas.json'
import deGeneralSettings from './locales/de/generalSettings.json'
import deStatistics from './locales/de/statistics.json'
import deQuickStatus from './locales/de/quickStatus.json'
import deVouchers from './locales/de/vouchers.json'
import deDashboard from './locales/de/dashboard.json'
import deNavigation from './locales/de/navigation.json'
import deAnnouncements from './locales/de/announcements.json'
import deAppSettings from './locales/de/appSettings.json'
import deMenuPrint from './locales/de/menuPrint.json'
import deLandingPage from './locales/de/landingPage.json'
import deOnboarding from './locales/de/onboarding.json'
import deDeployment from './locales/de/deployment.json'
import deAccountSecurity from './locales/de/accountSecurity.json'
import deDiagnostics from './locales/de/diagnostics.json'
import deReservations from './locales/de/reservations.json'
import deLoyalty from './locales/de/loyalty.json'
import deAppLinks from './locales/de/appLinks.json'
import dePosSetup from './locales/de/posSetup.json'
import dePosOrdering from './locales/de/posOrdering.json'
import deServiceContract from './locales/de/serviceContract.json'

export const supportedLanguages = ['de', 'en', 'es', 'ar'] as const
export type SupportedLanguage = (typeof supportedLanguages)[number]

export const languageConfig: Record<
  SupportedLanguage,
  {
    name: string
    nativeName: string
    flag: string
    dir: 'ltr' | 'rtl'
  }
> = {
  de: { name: 'German', nativeName: 'Deutsch', flag: 'DE', dir: 'ltr' },
  en: { name: 'English', nativeName: 'English', flag: 'GB', dir: 'ltr' },
  es: { name: 'Spanish', nativeName: 'Espanol', flag: 'ES', dir: 'ltr' },
  ar: { name: 'Arabic', nativeName: 'العربية', flag: 'SA', dir: 'rtl' },
}

const resources = {
  de: {
    common: deCommon,
    auth: deAuth,
    menuEditor: deMenuEditor,
    restaurants: deRestaurants,
    openingTimes: deOpeningTimes,
    holidaySettings: deHolidaySettings,
    orders: deOrders,
    deliveryAreas: deDeliveryAreas,
    generalSettings: deGeneralSettings,
    statistics: deStatistics,
    quickStatus: deQuickStatus,
    vouchers: deVouchers,
    dashboard: deDashboard,
    navigation: deNavigation,
    announcements: deAnnouncements,
    appSettings: deAppSettings,
    menuPrint: deMenuPrint,
    landingPage: deLandingPage,
    onboarding: deOnboarding,
    deployment: deDeployment,
    accountSecurity: deAccountSecurity,
    diagnostics: deDiagnostics,
    reservations: deReservations,
    loyalty: deLoyalty,
    appLinks: deAppLinks,
    posSetup: dePosSetup,
    posOrdering: dePosOrdering,
    serviceContract: deServiceContract,
  },
}

interface LocaleModule { default: Record<string, unknown> }
const lazyLocaleModules: Partial<Record<string, () => Promise<LocaleModule>>> =
  import.meta.glob<LocaleModule>([
    './locales/*/*.json',
    '!./locales/de/*.json',
  ])

async function loadLazyLocale(language: string, namespace: string) {
  const loader = lazyLocaleModules[`./locales/${language}/${namespace}.json`]
  if (!loader) throw new Error(`Unsupported locale resource: ${language}/${namespace}`)
  return (await loader()).default
}

void i18n
  .use(LanguageDetector)
  .use(
    resourcesToBackend(loadLazyLocale)
  )
  .use(initReactI18next)
  .init({
    resources,
    // Bundled languages (de) are used as-is; everything else loads lazily
    partialBundledLanguages: true,
    fallbackLng: 'de',
    supportedLngs: supportedLanguages,
    nonExplicitSupportedLngs: true,
    load: 'languageOnly',
    defaultNS: 'common',
    ns: ['common', 'auth', 'menuEditor', 'restaurants', 'openingTimes', 'holidaySettings', 'orders', 'deliveryAreas', 'generalSettings', 'statistics', 'quickStatus', 'vouchers', 'dashboard', 'navigation', 'announcements', 'appSettings', 'menuPrint', 'landingPage', 'onboarding', 'deployment', 'accountSecurity', 'diagnostics', 'reservations', 'loyalty', 'appLinks'],

    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'i18nextLng',
      caches: ['localStorage'],
    },

    interpolation: {
      escapeValue: false, // React already escapes
    },

    react: {
      useSuspense: false, // Avoid hydration issues
      // Re-render once a lazily-loaded language chunk arrives
      bindI18nStore: 'added',
    },
  })

export default i18n
